import { useMemo } from 'react'
import { GraphQL } from '@guestgoodie/graphql'
import { dateTime } from '@guestgoodie/helpers'
import { Trans, useLingui } from '@lingui/react/macro'

import { useEventOccurences } from '../hooks/useEventOccurences'
import Button from '@/components/ui/Button'
import EventOccurenceTile from './EventOccurenceTile'

const formatter = new Intl.DateTimeFormat(undefined, { dateStyle: 'full' })

interface OccurenceByDay {
  label: string
  occurences: GraphQL.EventOccurenceFragmentFragment[]
}

export default function Events() {
  const { t } = useLingui()

  const { occurences, loadBefore, loadAfter, loading } = useEventOccurences()

  const occurencesPerDay = useMemo(() => {
    const now = new Date()

    return occurences.reduce<OccurenceByDay[]>(function (acc, occurence) {
      const start = new Date(occurence.startAt)
      const isToday = dateTime.isSameDay(start, now)
      const day = dateTime.toDay(new Date(occurence.startAt))
      const label = isToday
        ? `${t`Today`} - ${formatter.format(day)}`
        : formatter.format(day)

      if (acc[acc.length - 1]?.label === label)
        acc[acc.length - 1].occurences.push(occurence)
      else acc.push({ label, occurences: [occurence] })
      return acc
    }, [])
  }, [occurences, t])

  return (
    <section className="flex h-full flex-col gap-5">
      <div className="flex flex-row items-center justify-between px-4">
        <h1 className="text-heading2 md:text-heading1 text-text">
          <Trans>Participants</Trans>
        </h1>
        <Button onClick={loadBefore} loading={loading}>
          <Trans>Load earlier</Trans>
        </Button>
      </div>
      <div className="flex flex-1 flex-col gap-2.5 overflow-y-auto px-5">
        {occurencesPerDay.map(function ({ label, occurences }) {
          return (
            <div key={label} className="flex flex-col gap-2.5">
              <div className="flex w-full flex-row items-end justify-start pl-4">
                <h2 className="text-heading3 md:text-heading2 text-text-light">
                  {label}
                </h2>
              </div>
              <div className="flex flex-col gap-2.5">
                {occurences.map(function (occurence) {
                  return (
                    <EventOccurenceTile
                      key={occurence.token}
                      event={occurence}
                    />
                  )
                })}
              </div>
            </div>
          )
        })}
        <div className="flex w-full flex-row justify-center py-10">
          <Button onClick={loadAfter} loading={loading}>
            <Trans>Load later</Trans>
          </Button>
        </div>
      </div>
    </section>
  )
}
